import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBullseye, FaColumns, FaPlus, FaSearch, FaEye, FaFileAlt, FaFileInvoiceDollar, FaArrowLeft, FaCheck, FaTimes, FaUserCheck, FaCircle } from 'react-icons/fa';
import { crmOpportunityApi } from '../../api/crmApi';
import AppDropdown from '../../components/AppDropdown';
import { LeadStatusBadge, PriorityBadge } from './components/LeadStatusBadge';
import './CRM.css';

const STAGE_OPTIONS = ['New', 'Contacted', 'Interested', 'Qualified', 'Requirement', 'Quotation', 'Negotiation', 'Won', 'Lost'].map((s) => ({ value: s, label: s }));
const FORM_STAGE_OPTIONS = ['New', 'Contacted', 'Interested', 'Qualified', 'Requirement', 'Quotation', 'Negotiation'].map((s) => ({ value: s, label: s }));
const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'].map((s) => ({ value: s, label: s }));
const SOURCE_OPTIONS = ['IndiaMART', 'Manual', 'Website', 'Referral', 'Other'].map((s) => ({ value: s, label: s }));

const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const fallbackOpps = [
  { id: 1, opportunityId: 'OPP-1041', customerName: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', mobile: '9876543210', email: 'ankit@mehtaoxygen.in', product: 'Oxygen Cylinder', quantity: 50, expectedValue: 82500, stage: 'Negotiation', priority: 'High', probability: 70, expectedCloseDate: '2026-10-20', salesperson: 'Rahul Sharma', source: 'IndiaMART', leadId: 1, ctsQuoteId: 'QT-2041', customerId: 101, notes: 'Bulk order for new plant, price negotiation at 5% discount requested.', createdAt: '2026-09-28' },
  { id: 2, opportunityId: 'OPP-1042', customerName: 'Rajesh Kumar', company: 'Kumar Industrial Gases', mobile: '9811122233', email: 'rajesh@kumarindgas.com', product: 'LPG Cylinder', quantity: 100, expectedValue: 145000, stage: 'Quotation', priority: 'High', probability: 50, expectedCloseDate: '2026-11-05', salesperson: 'Priya Verma', source: 'Website', leadId: 2, ctsQuoteId: 'QT-2043', customerId: null, notes: '', createdAt: '2026-09-30' },
  { id: 3, opportunityId: 'OPP-1043', customerName: 'Sunita Devi', company: 'Devi Gas Agency', mobile: '9765544332', email: 'sunita@devigas.in', product: 'Acetylene Cylinder', quantity: 20, expectedValue: 62000, stage: 'Qualified', priority: 'Medium', probability: 40, expectedCloseDate: '2026-10-15', salesperson: 'Amit Kumar', source: 'IndiaMART', leadId: 3, ctsQuoteId: '', customerId: null, notes: '', createdAt: '2026-09-25' },
  { id: 4, opportunityId: 'OPP-1044', customerName: 'Pooja Sharma', company: 'Sharma Medical Gas', mobile: '9777666555', email: 'pooja@sharmamedical.in', product: 'Medical Oxygen Cylinder', quantity: 80, expectedValue: 195000, stage: 'Negotiation', priority: 'High', probability: 65, expectedCloseDate: '2026-10-12', salesperson: 'Sneha Patel', source: 'IndiaMART', leadId: 5, ctsQuoteId: '', customerId: 105, notes: 'Government hospital tender, needs compliance docs.', createdAt: '2026-09-20' },
  { id: 5, opportunityId: 'OPP-1045', customerName: 'Deepak Joshi', company: 'Joshi Fabricators', mobile: '9654433221', email: 'deepak@joshifab.com', product: 'CO2 Cylinder', quantity: 40, expectedValue: 78000, stage: 'Won', priority: 'Medium', probability: 100, expectedCloseDate: '2026-10-01', salesperson: 'Rahul Sharma', source: 'Manual', leadId: 6, ctsQuoteId: 'QT-2038', customerId: 102, notes: 'Converted to SO-1180.', createdAt: '2026-09-15' },
  { id: 6, opportunityId: 'OPP-1046', customerName: 'Mohammed Ali', company: 'Ali Welding Works', mobile: '9898989898', email: 'ali@aliwelding.com', product: 'Argon Cylinder', quantity: 30, expectedValue: 45000, stage: 'New', priority: 'Low', probability: 10, expectedCloseDate: '2026-11-20', salesperson: 'Rahul Sharma', source: 'Referral', leadId: 4, ctsQuoteId: '', customerId: null, notes: '', createdAt: '2026-10-02' },
];

const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function CRMOpportunities() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  return id ? <OpportunityDetail navigate={navigate} id={id} /> :
    location.pathname.endsWith('/new') ? <OpportunityForm navigate={navigate} location={location} /> :
    <OpportunityList navigate={navigate} />;
}

/* ---------- LIST ---------- */
const fallbackList = [
  { id: 1, opportunityId: 'OPP-1041', customerName: 'Ankit Mehta', company: 'Mehta Oxygen Pvt. Ltd.', expectedValue: 82500, stage: 'Negotiation', probability: 70, expectedCloseDate: '2026-10-20', salesperson: 'Rahul Sharma', priority: 'High' },
  { id: 2, opportunityId: 'OPP-1042', customerName: 'Rajesh Kumar', company: 'Kumar Industrial Gases', expectedValue: 145000, stage: 'Quotation', probability: 50, expectedCloseDate: '2026-11-05', salesperson: 'Priya Verma', priority: 'High' },
  { id: 3, opportunityId: 'OPP-1043', customerName: 'Sunita Devi', company: 'Devi Gas Agency', expectedValue: 62000, stage: 'Qualified', probability: 40, expectedCloseDate: '2026-10-15', salesperson: 'Amit Kumar', priority: 'Medium' },
  { id: 4, opportunityId: 'OPP-1044', customerName: 'Pooja Sharma', company: 'Sharma Medical Gas', expectedValue: 195000, stage: 'Negotiation', probability: 65, expectedCloseDate: '2026-10-12', salesperson: 'Sneha Patel', priority: 'High' },
  { id: 5, opportunityId: 'OPP-1045', customerName: 'Deepak Joshi', company: 'Joshi Fabricators', expectedValue: 78000, stage: 'Won', probability: 100, expectedCloseDate: '2026-10-01', salesperson: 'Rahul Sharma', priority: 'Medium' },
  { id: 6, opportunityId: 'OPP-1046', customerName: 'Mohammed Ali', company: 'Ali Welding Works', expectedValue: 45000, stage: 'New', probability: 10, expectedCloseDate: '2026-11-20', salesperson: 'Rahul Sharma', priority: 'Low' },
];

const OpportunityList = ({ navigate }) => {
  const [opps, setOpps] = useState(fallbackList);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await crmOpportunityApi.getAll({ size: 200 });
        const d = Array.isArray(res.data) ? res.data : res.data?.content;
        if (d?.length) setOpps(d);
      } catch { /* fallback */ } finally { setLoading(false); }
    })();
  }, []);

  const filtered = opps.filter((o) => {
    if (stageFilter && o.stage !== stageFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (o.customerName || '').toLowerCase().includes(q) || (o.company || '').toLowerCase().includes(q) || (o.opportunityId || '').toLowerCase().includes(q);
    }
    return true;
  });

  const openValue = opps.filter((o) => !['Won', 'Lost'].includes(o.stage)).reduce((s, o) => s + Number(o.expectedValue || 0), 0);

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaBullseye aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">Opportunities</h1>
            <p className="crm-subtitle">Deals in motion — quotation, negotiate, win</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-outline" onClick={() => navigate('/crm/pipeline')}><FaColumns aria-hidden="true" /> Pipeline Board</button>
          <button className="crm-btn crm-btn-primary" onClick={() => navigate('/crm/opportunities/new')}><FaPlus aria-hidden="true" /> New Opportunity</button>
        </div>
      </div>

      <div className="crm-kpi-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        {loading ? Array.from({ length: 4 }).map((_, i) => (
          <div className="crm-kpi-card" key={`esk-${i}`}>
            <div style={{ width: '100%' }}>
              <SkeletonBlock width={70} height={22} radius={6} style={{ marginBottom: 8 }} />
              <SkeletonBlock width={90} height={12} radius={6} />
            </div>
          </div>
        )) : [
          { label: 'Open Value', value: money(openValue), color: '#6366F1' },
          { label: 'Total', value: opps.length, color: '#0EA5E9' },
          { label: 'Won', value: opps.filter((o) => o.stage === 'Won').length, color: '#16A34A' },
          { label: 'Lost', value: opps.filter((o) => o.stage === 'Lost').length, color: '#94A3B8' },
        ].map((k) => (
          <div className="crm-kpi-card" key={k.label}>
            <div><div className="crm-kpi-value" style={{ color: k.color }}>{k.value}</div><div className="crm-kpi-label">{k.label}</div></div>
          </div>
        ))}
      </div>

      <div className="crm-filters">
        <div className="crm-search-box">
          <FaSearch className="crm-search-icon" aria-hidden="true" />
          <input
            type="text"
            aria-label="Search opportunities"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search opportunity, customer..."
          />
        </div>
        <div className="crm-status-filter" role="group" aria-label="Filter opportunities by stage">
          <AppDropdown value={stageFilter} options={STAGE_OPTIONS} onChange={setStageFilter}
            placeholder="All Stages" menuWidth={200} menuClassName="crm-dropdown-window" />
        </div>
        <button className="crm-btn crm-btn-sm crm-btn-ghost" onClick={() => { setSearch(''); setStageFilter(''); }}>Clear</button>
      </div>

      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead><tr><th>Opportunity</th><th>Customer</th><th>Value</th><th>Stage</th><th>Priority</th><th>Probability</th><th>Close Date</th><th>Salesperson</th><th>Actions</th></tr></thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, r) => (
                <tr key={`skeleton-${r}`}>
                  {Array.from({ length: 9 }).map((__, c) => (
                    <td key={c}><SkeletonBlock width={c === 0 ? 110 : '80%'} height={16} radius={6} /></td>
                  ))}
                </tr>
              ))
            ) : (
              <>
            {filtered.map((o) => (
              <tr key={o.id}>
                <td className="link-cell" onClick={() => navigate(`/crm/opportunities/${o.id}`)}>{o.opportunityId}</td>
                <td><strong>{o.customerName}</strong><div style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{o.company}</div></td>
                <td style={{ fontWeight: 700 }}>{money(o.expectedValue)}</td>
                <td><LeadStatusBadge status={o.stage} /></td>
                <td><PriorityBadge priority={o.priority} /></td>
                <td>{o.probability}%</td>
                <td>{fmt(o.expectedCloseDate)}</td>
                <td>{o.salesperson || '—'}</td>
                <td><div className="crm-actions">
                  <button className="crm-icon-btn view" title="View" aria-label="View opportunity" onClick={() => navigate(`/crm/opportunities/${o.id}`)}><FaEye aria-hidden="true" /></button>
                  <button className="crm-icon-btn quote" title="Quotation" aria-label="Create quotation" onClick={() => navigate('/dashboard/estimates/new', { state: { crmOpportunityId: o.id, opportunity: o } })}><FaFileAlt aria-hidden="true" /></button>
                  {o.stage === 'Won' && <button className="crm-icon-btn convert" title="Sales Order" aria-label="Create sales order" onClick={() => navigate('/dashboard/sales-orders/new', { state: { crmOpportunityId: o.id, opportunity: o } })}><FaFileInvoiceDollar aria-hidden="true" /></button>}
                </div></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={9}><div className="crm-empty"><div className="crm-empty-icon"><FaBullseye aria-hidden="true" /></div><div className="crm-empty-title">No opportunities</div><div className="crm-empty-desc">Create an opportunity from a qualified lead.</div></div></td></tr>}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ---------- DETAIL ---------- */
const OpportunityDetail = ({ navigate, id }) => {
  const [opp, setOpp] = useState(null);
  const [quotations, setQuotations] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await crmOpportunityApi.getById(id);
        setOpp(res.data || fallbackOpps.find((o) => String(o.id) === String(id)) || fallbackOpps[0]);
      } catch {
        setOpp(fallbackOpps.find((o) => String(o.id) === String(id)) || fallbackOpps[0]);
      }
      try {
        const q = await crmOpportunityApi.getQuotations(id);
        if (Array.isArray(q.data)) setQuotations(q.data);
      } catch { setQuotations([]); }
    })();
  }, [id]);

  if (!opp) return <div style={{ padding: 30, color: 'var(--muted)' }}>Loading...</div>;

  const stageUpdate = async (stage) => {
    setOpp({ ...opp, stage });
    try { await crmOpportunityApi.updateStage(opp.id, { stage }); toast.success(`Stage → ${stage}`); }
    catch { toast.error('Failed to update stage.'); }
  };

  const markWon = async () => {
    if (!window.confirm(`Mark "${opp.opportunityId}" as Won? You can then create a Sales Order.`)) return;
    try { await crmOpportunityApi.markWon(opp.id, { wonAt: new Date().toISOString() }); } catch {}
    setOpp({ ...opp, stage: 'Won', probability: 100 });
    toast.success('Opportunity won! Create a Sales Order to fulfill it.');
  };

  const markLost = async () => {
    const reason = window.prompt('Why was this opportunity lost?', '');
    if (reason === null) return;
    try { await crmOpportunityApi.markLost(opp.id, { reason }); } catch {}
    setOpp({ ...opp, stage: 'Lost', probability: 0, notes: reason });
    toast.success('Opportunity marked lost.');
  };

  const convertToCustomer = async () => {
    if (!window.confirm('Convert this opportunity to a CTS Customer?')) return;
    try { await crmOpportunityApi.convertToCustomer(opp.id, { customerId: opp.customerId || null }); } catch {}
    toast.success('Redirecting to customer creation...');
    navigate('/dashboard/customers/new', { state: { fromCrm: true, opportunity: opp } });
  };

  const createQuotation = () => navigate('/dashboard/estimates/new', { state: { crmOpportunityId: opp.id, opportunity: opp } });
  const createSO = () => navigate('/dashboard/sales-orders/new', { state: { crmOpportunityId: opp.id, opportunity: opp } });

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaBullseye aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">{opp.opportunityId} · {opp.customerName}</h1>
            <p className="crm-subtitle">{opp.company} · {opp.product} × {opp.quantity}</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-ghost" onClick={() => navigate('/crm/opportunities')}><FaArrowLeft aria-hidden="true" /> Back</button>
          <button className="crm-btn crm-btn-outline" onClick={() => navigate(`/crm/leads/${opp.leadId}`)}>View Lead</button>
          <button className="crm-btn crm-btn-outline" onClick={createQuotation}><FaFileAlt aria-hidden="true" /> Create Quotation</button>
          {opp.stage === 'Won' && <button className="crm-btn crm-btn-primary" onClick={createSO}><FaFileInvoiceDollar aria-hidden="true" /> Create Sales Order</button>}
          {opp.stage !== 'Won' && opp.stage !== 'Lost' && <button className="crm-btn crm-btn-primary" onClick={markWon}><FaCheck aria-hidden="true" /> Mark Won</button>}
          {opp.stage !== 'Won' && opp.stage !== 'Lost' && <button className="crm-btn crm-btn-danger" onClick={markLost}><FaTimes aria-hidden="true" /> Mark Lost</button>}
        </div>
      </div>

      <div className="crm-detail-grid">
        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Opportunity Details</div>
          <div className="crm-info-row"><span>Status</span><strong><LeadStatusBadge status={opp.stage} /></strong></div>
          <div className="crm-info-row"><span>Expected Value</span><strong style={{ color: 'var(--success)' }}>{money(opp.expectedValue)}</strong></div>
          <div className="crm-info-row"><span>Probability</span><strong>{opp.probability}%</strong></div>
          <div className="crm-info-row"><span>Priority</span><strong><PriorityBadge priority={opp.priority} /></strong></div>
          <div className="crm-info-row"><span>Expected Close</span><strong>{fmt(opp.expectedCloseDate)}</strong></div>
          <div className="crm-info-row"><span>Salesperson</span><strong>{opp.salesperson || '—'}</strong></div>
          <div className="crm-info-row"><span>Source</span><strong>{opp.source || '—'}</strong></div>
          <div className="crm-info-row"><span>Created</span><strong>{fmt(opp.createdAt)}</strong></div>
        </div>

        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Customer Details</div>
          <div className="crm-info-row"><span>Name</span><strong>{opp.customerName}</strong></div>
          <div className="crm-info-row"><span>Company</span><strong>{opp.company}</strong></div>
          <div className="crm-info-row"><span>Mobile</span><strong>{opp.mobile || '—'}</strong></div>
          <div className="crm-info-row"><span>Email</span><strong>{opp.email || '—'}</strong></div>
          <div className="crm-info-row"><span>CTS Customer</span>
            <strong>{opp.customerId ? <span style={{ color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><FaCheck aria-hidden="true" /> Linked #{opp.customerId}</span> : <span style={{ color: 'var(--muted)' }}>Not linked</span>}</strong>
          </div>
          <div style={{ marginTop: 12 }}>
            <button className="crm-btn crm-btn-outline crm-btn-sm" onClick={convertToCustomer}><FaUserCheck aria-hidden="true" /> Convert to Customer</button>
          </div>
        </div>

        <div className="crm-panel-card">
          <div className="crm-panel-card-title">Move Stage</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['New', 'Contacted', 'Interested', 'Qualified', 'Requirement', 'Quotation', 'Negotiation'].map((s) => (
              <button key={s} className={`crm-btn crm-btn-sm ${opp.stage === s ? 'crm-btn-primary' : 'crm-btn-outline'}`}
                onClick={() => stageUpdate(s)} disabled={opp.stage === 'Won' || opp.stage === 'Lost'}>
                {opp.stage === s && <FaCircle aria-hidden="true" style={{ fontSize: '0.5em', marginRight: 6 }} />}{s}
              </button>
            ))}
          </div>
        </div>

        <div className="crm-panel-card">
          <div className="crm-panel-card-title">CTS Quotations</div>
          {opp.ctsQuoteId && (
            <div className="crm-info-row"><span>Linked Quote</span><strong>{opp.ctsQuoteId}</strong></div>
          )}
          {quotations.map((q) => (
            <div className="crm-info-row" key={q.id}><span>{q.quoteNumber || q.id}</span>
              <strong>{money(q.totalAmount)}</strong></div>
          ))}
          {!opp.ctsQuoteId && !quotations.length && (
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>No CTS quotation linked yet.</div>
          )}
          <div style={{ marginTop: 12 }}>
            <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={createQuotation}><FaFileAlt aria-hidden="true" /> Create Quotation (CTS)</button>
          </div>
        </div>
      </div>

      {opp.notes && (
        <div className="crm-panel-card" style={{ marginTop: 16 }}>
          <div className="crm-panel-card-title">Notes</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--ink-soft)' }}>{opp.notes}</div>
        </div>
      )}
    </div>
  );
};

/* ---------- FORM (create / edit) ---------- */
const OpportunityForm = ({ navigate, location }) => {
  const seed = location.state?.opportunity || location.state?.lead || {};
  const [form, setForm] = useState({
    customerName: seed.customerName || seed.name || '',
    company: seed.company || '',
    mobile: seed.mobile || '',
    email: seed.email || '',
    product: seed.product || '',
    quantity: seed.quantity || 1,
    expectedValue: seed.expectedValue || '',
    stage: seed.stage || 'New',
    priority: seed.priority || 'Medium',
    probability: seed.probability ?? 10,
    expectedCloseDate: seed.expectedCloseDate || '',
    salesperson: seed.assignedTo || seed.salesperson || '',
    source: seed.source || 'Manual',
    leadId: seed.id || location.state?.leadId || '',
    notes: seed.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!form.customerName.trim()) { toast.error('Customer name is required.'); return; }
    if (!form.expectedValue) { toast.error('Expected value is required.'); return; }
    setSaving(true);
    try {
      const res = await crmOpportunityApi.create(form);
      toast.success(`Opportunity ${res.data?.opportunityId || ''} created.`);
      navigate('/crm/opportunities');
    } catch {
      toast.info('Saved locally (CRM API not reachable).');
      navigate('/crm/opportunities');
    } finally { setSaving(false); }
  };

  const field = (k, label, type = 'text', req = false) => (
    <div className="crm-field">
      <label>{label} {req && <span className="req">*</span>}</label>
      <input type={type} value={form[k]} onChange={set(k)} />
    </div>
  );

  return (
    <div className="crm-page">
      <div className="crm-header">
        <div className="crm-header-left">
          <div className="crm-page-icon"><FaBullseye aria-hidden="true" /></div>
          <div>
            <h1 className="crm-title">{location.state?.leadId ? 'New Opportunity from Lead' : 'New Opportunity'}</h1>
            <p className="crm-subtitle">Track a deal from requirement → quotation → won</p>
          </div>
        </div>
        <div className="crm-header-actions">
          <button className="crm-btn crm-btn-ghost" onClick={() => navigate('/crm/opportunities')}><FaArrowLeft aria-hidden="true" /> Cancel</button>
          <button className="crm-btn crm-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : <><FaPlus aria-hidden="true" /> Create Opportunity</>}</button>
        </div>
      </div>

      <div className="crm-panel-card">
        <div className="crm-panel-card-title">Customer</div>
        <div className="crm-form-grid">
          {field('customerName', 'Customer Name', 'text', true)}
          {field('company', 'Company')}
          {field('mobile', 'Mobile')}
          {field('email', 'Email', 'email')}
        </div>
      </div>

      <div className="crm-panel-card" style={{ marginTop: 16 }}>
        <div className="crm-panel-card-title">Deal</div>
        <div className="crm-form-grid">
          {field('product', 'Product / Service', 'text', true)}
          {field('quantity', 'Quantity', 'number')}
          {field('expectedValue', 'Expected Value (₹)', 'number', true)}
          <div className="crm-field">
            <label>Stage <span className="req">*</span></label>
            <AppDropdown value={form.stage} options={FORM_STAGE_OPTIONS}
              onChange={(v) => setForm((p) => ({ ...p, stage: v }))}
              menuWidth={200} menuClassName="crm-dropdown-window" />
          </div>
          <div className="crm-field">
            <label>Priority</label>
            <AppDropdown value={form.priority} options={PRIORITY_OPTIONS}
              onChange={(v) => setForm((p) => ({ ...p, priority: v }))}
              menuWidth={180} menuClassName="crm-dropdown-window" />
          </div>
          <div className="crm-field">
            <label>Probability (%)</label>
            <input type="number" min="0" max="100" value={form.probability} onChange={set('probability')} />
          </div>
          {field('expectedCloseDate', 'Expected Close Date', 'date')}
          {field('salesperson', 'Salesperson')}
          <div className="crm-field">
            <label>Source</label>
            <AppDropdown value={form.source} options={SOURCE_OPTIONS}
              onChange={(v) => setForm((p) => ({ ...p, source: v }))}
              menuWidth={200} menuClassName="crm-dropdown-window" />
          </div>
          <div className="crm-field crm-field-full">
            <label>Notes</label>
            <textarea value={form.notes} onChange={set('notes')} rows={3} placeholder="Negotiation details, requirements, timelines..." />
          </div>
        </div>
      </div>
    </div>
  );
};
